import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { 
  Key, Plus, Copy, Eye, EyeOff, Trash2, Settings, BarChart3, 
  AlertCircle, CheckCircle2, Clock, Globe, Shield, Loader2 
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPartnerKeysPage() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [newKeyData, setNewKeyData] = useState(null);
  const [keyStats, setKeyStats] = useState(null);
  
  const [formData, setFormData] = useState({
    partnerName: '',
    partnerEmail: '',
    allowedDomains: '',
    requestsPerMinute: 60,
    requestsPerDay: 10000
  });

  const token = localStorage.getItem('admin_token');

  const fetchKeys = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/admin/partner-keys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKeys(response.data.keys || []);
    } catch (err) {
      console.error('Failed to fetch keys:', err);
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleCreateKey = async () => {
    try {
      const domains = formData.allowedDomains
        .split('\n')
        .map(d => d.trim())
        .filter(d => d);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/partner-keys`,
        {
          partnerName: formData.partnerName,
          partnerEmail: formData.partnerEmail,
          allowedDomains: domains,
          requestsPerMinute: formData.requestsPerMinute,
          requestsPerDay: formData.requestsPerDay
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNewKeyData(response.data);
      setCreateDialogOpen(false);
      fetchKeys();
      toast.success('API key created successfully');
      
      // Reset form
      setFormData({
        partnerName: '',
        partnerEmail: '',
        allowedDomains: '',
        requestsPerMinute: 60,
        requestsPerDay: 10000
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create API key');
    }
  };

  const handleUpdateKey = async () => {
    if (!selectedKey) return;
    
    try {
      const domains = formData.allowedDomains
        .split('\n')
        .map(d => d.trim())
        .filter(d => d);
      
      await axios.put(
        `${BACKEND_URL}/api/admin/partner-keys/${selectedKey.id}`,
        {
          partnerName: formData.partnerName,
          partnerEmail: formData.partnerEmail,
          allowedDomains: domains,
          requestsPerMinute: formData.requestsPerMinute,
          requestsPerDay: formData.requestsPerDay
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setEditDialogOpen(false);
      setSelectedKey(null);
      fetchKeys();
      toast.success('API key updated');
    } catch (err) {
      toast.error('Failed to update API key');
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (!confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/partner-keys/${keyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchKeys();
      toast.success('API key revoked');
    } catch (err) {
      toast.error('Failed to revoke API key');
    }
  };

  const handleViewStats = async (key) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/partner-keys/${key.id}/stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setKeyStats(response.data);
      setSelectedKey(key);
      setStatsDialogOpen(true);
    } catch (err) {
      toast.error('Failed to load stats');
    }
  };

  const openEditDialog = (key) => {
    setSelectedKey(key);
    setFormData({
      partnerName: key.partnerName,
      partnerEmail: key.partnerEmail,
      allowedDomains: (key.allowedDomains || []).join('\n'),
      requestsPerMinute: key.rateLimit?.requestsPerMinute || 60,
      requestsPerDay: key.rateLimit?.requestsPerDay || 10000
    });
    setEditDialogOpen(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Key className="w-7 h-7 text-orange-600" />
              Partner API Keys
            </h1>
            <p className="text-gray-600 mt-1">
              Manage API keys for partner integrations and embedded widgets
            </p>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-orange-600 hover:bg-orange-700">
                <Plus className="w-4 h-4 mr-2" />
                Create API Key
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Create Partner API Key</DialogTitle>
                <DialogDescription>
                  Generate a new API key for a partner integration.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="partnerName">Partner Name *</Label>
                  <Input
                    id="partnerName"
                    value={formData.partnerName}
                    onChange={(e) => setFormData({ ...formData, partnerName: e.target.value })}
                    placeholder="e.g., TechVenue UK"
                  />
                </div>
                <div>
                  <Label htmlFor="partnerEmail">Partner Email *</Label>
                  <Input
                    type="email"
                    id="partnerEmail"
                    value={formData.partnerEmail}
                    onChange={(e) => setFormData({ ...formData, partnerEmail: e.target.value })}
                    placeholder="api@partner.com"
                  />
                </div>
                <div>
                  <Label htmlFor="allowedDomains">Allowed Domains (one per line)</Label>
                  <Textarea
                    id="allowedDomains"
                    value={formData.allowedDomains}
                    onChange={(e) => setFormData({ ...formData, allowedDomains: e.target.value })}
                    placeholder="partner.com&#10;*.partner.com"
                    rows={3}
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty to allow all domains. Supports wildcards.</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="rpm">Requests/Minute</Label>
                    <Input
                      type="number"
                      id="rpm"
                      value={formData.requestsPerMinute}
                      onChange={(e) => setFormData({ ...formData, requestsPerMinute: parseInt(e.target.value) || 60 })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="rpd">Requests/Day</Label>
                    <Input
                      type="number"
                      id="rpd"
                      value={formData.requestsPerDay}
                      onChange={(e) => setFormData({ ...formData, requestsPerDay: parseInt(e.target.value) || 10000 })}
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                <Button 
                  onClick={handleCreateKey}
                  disabled={!formData.partnerName || !formData.partnerEmail}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  Create Key
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* New Key Display */}
        {newKeyData && (
          <Card className="border-green-200 bg-green-50">
            <CardHeader>
              <CardTitle className="text-green-800 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                API Key Created Successfully
              </CardTitle>
              <CardDescription className="text-green-700">
                Copy and save this key securely. It will not be shown again.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-white rounded-lg p-4 border border-green-200">
                <div className="flex items-center justify-between">
                  <code className="text-sm font-mono text-gray-800 break-all">{newKeyData.apiKey}</code>
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(newKeyData.apiKey)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <Button 
                variant="outline" 
                className="mt-4" 
                onClick={() => setNewKeyData(null)}
              >
                Dismiss
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Keys List */}
        <Card>
          <CardHeader>
            <CardTitle>Active API Keys</CardTitle>
            <CardDescription>
              {keys.filter(k => k.status === 'active').length} active keys
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : keys.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Key className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No API keys created yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Partner</TableHead>
                    <TableHead>Key Prefix</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Usage Today</TableHead>
                    <TableHead>Last Used</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {keys.map((key) => (
                    <TableRow key={key.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{key.partnerName}</p>
                          <p className="text-sm text-gray-500">{key.partnerEmail}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                          {key.keyPrefix}
                        </code>
                      </TableCell>
                      <TableCell>
                        {key.status === 'active' ? (
                          <Badge className="bg-green-100 text-green-800">Active</Badge>
                        ) : (
                          <Badge variant="destructive">Revoked</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <span className="font-medium">{key.todayStats?.impressions || 0}</span>
                          <span className="text-gray-500"> imp / </span>
                          <span className="font-medium">{key.todayStats?.clicks || 0}</span>
                          <span className="text-gray-500"> clicks</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {key.lastUsedAt ? formatDate(key.lastUsedAt) : 'Never'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewStats(key)}
                          >
                            <BarChart3 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditDialog(key)}
                            disabled={key.status !== 'active'}
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRevokeKey(key.id)}
                            disabled={key.status !== 'active'}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Edit API Key Settings</DialogTitle>
              <DialogDescription>
                Update settings for {selectedKey?.partnerName}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="edit-partnerName">Partner Name</Label>
                <Input
                  id="edit-partnerName"
                  value={formData.partnerName}
                  onChange={(e) => setFormData({ ...formData, partnerName: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-partnerEmail">Partner Email</Label>
                <Input
                  type="email"
                  id="edit-partnerEmail"
                  value={formData.partnerEmail}
                  onChange={(e) => setFormData({ ...formData, partnerEmail: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-allowedDomains">Allowed Domains (one per line)</Label>
                <Textarea
                  id="edit-allowedDomains"
                  value={formData.allowedDomains}
                  onChange={(e) => setFormData({ ...formData, allowedDomains: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-rpm">Requests/Minute</Label>
                  <Input
                    type="number"
                    id="edit-rpm"
                    value={formData.requestsPerMinute}
                    onChange={(e) => setFormData({ ...formData, requestsPerMinute: parseInt(e.target.value) || 60 })}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-rpd">Requests/Day</Label>
                  <Input
                    type="number"
                    id="edit-rpd"
                    value={formData.requestsPerDay}
                    onChange={(e) => setFormData({ ...formData, requestsPerDay: parseInt(e.target.value) || 10000 })}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdateKey} className="bg-orange-600 hover:bg-orange-700">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Stats Dialog */}
        <Dialog open={statsDialogOpen} onOpenChange={setStatsDialogOpen}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>API Key Statistics</DialogTitle>
              <DialogDescription>
                Performance metrics for {selectedKey?.partnerName}
              </DialogDescription>
            </DialogHeader>
            {keyStats && (
              <div className="space-y-6 py-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-blue-700">
                      {keyStats.summary?.totalImpressions?.toLocaleString() || 0}
                    </p>
                    <p className="text-sm text-blue-600">Total Impressions</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-green-700">
                      {keyStats.summary?.totalClicks?.toLocaleString() || 0}
                    </p>
                    <p className="text-sm text-green-600">Total Clicks</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-orange-700">
                      {keyStats.summary?.avgCtr || 0}%
                    </p>
                    <p className="text-sm text-orange-600">Avg CTR</p>
                  </div>
                </div>

                {/* Top Events */}
                {keyStats.topEvents && keyStats.topEvents.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Top Performing Events</h4>
                    <div className="space-y-2">
                      {keyStats.topEvents.slice(0, 5).map((event, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-gray-50 rounded p-2">
                          <span className="text-sm">{event.eventName || event._id}</span>
                          <div className="text-sm text-gray-500">
                            {event.impressions} imp / {event.clicks} clicks
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Daily Stats */}
                {keyStats.dailyStats && keyStats.dailyStats.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Last 7 Days</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2">Date</th>
                            <th className="text-right py-2">Impressions</th>
                            <th className="text-right py-2">Clicks</th>
                            <th className="text-right py-2">CTR</th>
                          </tr>
                        </thead>
                        <tbody>
                          {keyStats.dailyStats.slice(-7).reverse().map((day, idx) => (
                            <tr key={idx} className="border-b border-gray-100">
                              <td className="py-2">{day.date}</td>
                              <td className="text-right">{day.impressions || 0}</td>
                              <td className="text-right">{day.clicks || 0}</td>
                              <td className="text-right">{((day.ctr || 0) * 100).toFixed(2)}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setStatsDialogOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Documentation Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-600" />
              API Documentation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="endpoints">
              <TabsList>
                <TabsTrigger value="endpoints">Endpoints</TabsTrigger>
                <TabsTrigger value="embed">Embed Code</TabsTrigger>
              </TabsList>
              
              <TabsContent value="endpoints" className="space-y-4 mt-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium mb-2">GET /api/v1/recommendations</h4>
                  <p className="text-sm text-gray-600 mb-2">Get personalized event recommendations</p>
                  <code className="text-xs bg-gray-200 p-2 rounded block">
                    {`curl -H "X-Jigsaw-API-Key: YOUR_KEY" "${BACKEND_URL}/api/v1/recommendations?city=london&limit=6"`}
                  </code>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium mb-2">GET /api/v1/widget</h4>
                  <p className="text-sm text-gray-600 mb-2">Get embeddable HTML widget</p>
                  <code className="text-xs bg-gray-200 p-2 rounded block">
                    {`curl -H "X-Jigsaw-API-Key: YOUR_KEY" "${BACKEND_URL}/api/v1/widget?theme=light&style=grid"`}
                  </code>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium mb-2">POST /api/v1/track</h4>
                  <p className="text-sm text-gray-600 mb-2">Track impressions and clicks</p>
                  <code className="text-xs bg-gray-200 p-2 rounded block whitespace-pre-wrap">
{`curl -X POST -H "X-Jigsaw-API-Key: YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"type":"click","eventId":"event-uuid"}' \\
  "${BACKEND_URL}/api/v1/track"`}
                  </code>
                </div>
              </TabsContent>
              
              <TabsContent value="embed" className="mt-4">
                <div className="bg-gray-900 rounded-lg p-4 text-green-400 font-mono text-xs overflow-x-auto">
                  <pre>{`<!-- Jigsaw Events Widget -->
<div id="jigsaw-events"></div>
<script>
(function() {
  var config = {
    apiKey: 'YOUR_API_KEY_HERE',
    container: '#jigsaw-events',
    sectors: ['Technology'],
    city: 'london',
    limit: 4,
    theme: 'light',
    style: 'grid'
  };
  
  // Load widget
  var iframe = document.createElement('iframe');
  iframe.src = '${BACKEND_URL}/api/v1/widget' +
    '?sectors=' + config.sectors.join(',') +
    '&city=' + config.city +
    '&limit=' + config.limit +
    '&theme=' + config.theme +
    '&style=' + config.style;
  iframe.style.width = '100%';
  iframe.style.border = 'none';
  iframe.style.minHeight = '300px';
  document.querySelector(config.container).appendChild(iframe);
})();
</script>`}</pre>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Note: Replace YOUR_API_KEY_HERE with your actual API key. 
                  For iframe embeds, add the API key to the widget URL as a header.
                </p>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
